import { Button } from '@roger/ui-components';
import { X } from 'lucide-react';
import { useStore } from '../../store';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import { closeAndFocusDock } from '../../hooks/chat/chatFocus';
import { useChannel, useInbox } from '../../hooks/chat/useChatQueries';
import { fitName } from './fitText';
import { FittedText } from './FittedText';
import { MentionBadge } from './MentionBadge';
import { UnreadPill } from './UnreadPill';
import { WindowBadge } from './WindowBadge';

/** A minimized conversation: 210 x 44, restores on press, closes on the X. */
export function ChatTab({ id }: { id: string }) {
  const openChatWindow = useStore(state => state.openChatWindow);
  const isNew = id === NEW_MESSAGE_WINDOW;
  const { data: inbox } = useInbox();
  const row = inbox?.find(r => r.channel_id === id);
  // Without an inbox row (archived, restored from a past session) the cached
  // header names the tab.
  const { data: header } = useChannel(isNew || row ? null : id);

  const title = isNew
    ? 'New message'
    : (row?.title ?? header?.title ?? 'Conversation');
  const unread = row?.unread_count ?? 0;
  const mentioned = row?.has_unread_mention ?? false;
  const patient = row?.patient ?? header?.patient_card;

  return (
    <div
      data-chat-surface=''
      className='pointer-events-auto flex h-11 w-[210px] items-center rounded-t-[10px] border border-b-0 bg-background'
    >
      <button
        type='button'
        data-chat-tab={id}
        aria-label={`${title}${unread > 0 ? `, ${unread} unread` : ''}${mentioned ? ', mentioned you' : ''}`}
        onClick={() => openChatWindow(id)}
        className='flex h-full min-w-0 flex-1 items-center gap-2 rounded-tl-[10px] pl-3 pr-1 text-left hover:bg-muted'
      >
        <WindowBadge id={id} size='sm' />
        {patient ? (
          <span className='min-w-0 flex-1'>
            <FittedText
              text={title}
              fit={fits => fitName(patient.first_name, patient.last_name, fits)}
              className='text-[13px] font-medium'
            />
            <span className='block truncate text-[11px] text-muted-foreground'>
              Patient care team
            </span>
          </span>
        ) : (
          <span className='min-w-0 flex-1 truncate text-[13px] font-medium'>
            {title}
          </span>
        )}
        {mentioned && <MentionBadge />}
        <UnreadPill count={unread} />
      </button>
      <Button
        size='icon'
        variant='ghost'
        aria-label={`Close ${title}`}
        title='Close'
        onClick={() => closeAndFocusDock(id)}
      >
        <X className='h-3.5 w-3.5' />
      </Button>
    </div>
  );
}
